import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
} from "shards-react";
import { useSelector, useDispatch } from 'react-redux';
import { dashboardActions } from '../actions';
import CheckPermission from "../components/checkPermission/CheckPermission";

import PageTitle from "../components/common/PageTitle";
import UsersOverview from "../components/charts/UsersOverview";
import PaymentOverview from "../components/charts/PaymentOverview";
import MonthlyUsers from "../components/charts/MonthlyUsers";
import SubscriptionByPlan from "../components/charts/SubscriptionByPlan";
import RetentionOverview from "../components/charts/RetentionOverview";
import {
  DASHBOARD_RANGE_OPTIONS,
  DEFAULT_DASHBOARD_RANGE,
  formatDashboardUpdatedAt,
  getDashboardDateRange,
} from "../utils/dashboard";

const StateCard = ({ hoverTitle, className, label, value }) => (
  <div className="stateCard" title={hoverTitle}>
    <span className={`stats-small__label text-uppercase mb-1 ${className || ''}`}>
      {label}
    </span>
    <h6>{value}</h6>
  </div>
);

const formatNumber = value => Number(value || 0).toLocaleString('en-US');

StateCard.propTypes = {
  hoverTitle: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const SkeletonBlock = ({ height }) => (
  <div
    className="bg-light rounded w-100"
    style={{ height }}
    aria-hidden="true"
  />
);

SkeletonBlock.propTypes = {
  height: PropTypes.string.isRequired,
};

const DashboardSkeleton = () => (
  <>
    <Row className="mb-4" aria-label="Loading dashboard summary">
      {[0, 1, 2, 3, 4, 5].map(item => (
        <Col lg="2" md="4" sm="6" className="mb-3" key={item}>
          <Card small>
            <CardBody>
              <SkeletonBlock height="12px" />
              <div className="mt-3">
                <SkeletonBlock height="24px" />
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
    <Row aria-label="Loading dashboard charts">
      <Col lg="8" md="12" className="mb-4">
        <Card small>
          <CardBody>
            <SkeletonBlock height="260px" />
          </CardBody>
        </Card>
      </Col>
      <Col lg="4" md="12" className="mb-4">
        <Card small>
          <CardBody>
            <SkeletonBlock height="260px" />
          </CardBody>
        </Card>
      </Col>
    </Row>
  </>
);

const BlogOverview = () => {
  const dispatch = useDispatch();
  const [range, setRange] = useState(DEFAULT_DASHBOARD_RANGE);
  const overviewState = useSelector(state => state.dashboard.overview);
  const overviewQuery = useMemo(() => getDashboardDateRange(range), [range]);
  const overview = overviewState.data;
  const summary = overview?.summary;

  useEffect(() => {
    dispatch(dashboardActions.getOverview(overviewQuery));
  }, [dispatch, overviewQuery]);

  const paymentTotals = summary?.payments?.totalsByCurrency || {};
  const paymentHoverTitle = [
    `${Number(paymentTotals.IRR || 0).toLocaleString()} IRR`,
    `${Number(paymentTotals.EUR || 0).toLocaleString()} EUR`,
    paymentTotals.USD
      ? `${Number(paymentTotals.USD).toLocaleString()} USD`
      : null,
  ].filter(Boolean).join(' | ');
  const selectedRange = DASHBOARD_RANGE_OPTIONS.find(
    item => item.value === range
  );
  const hasInitialLoad = !overview && !overviewState.error;

  const refreshDashboard = () => {
    dispatch(dashboardActions.getOverview({
      ...overviewQuery,
      refresh: true,
    }));
  };

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle title="Overview" subtitle="Dashboard" className="text-sm-left mb-3" />
      </Row>

      <Row className="mb-3 align-items-center">
        <Col md="6" className="mb-2 mb-md-0">
          <div className="text-muted small">
            {selectedRange?.label || 'Dashboard range'}
            {' - '}
            Last updated: {formatDashboardUpdatedAt(overviewState.lastUpdated)}
            {overviewState.loading && overview ? ' - Updating...' : ''}
          </div>
        </Col>
        <Col md="6">
          <div className="d-flex justify-content-md-end align-items-center">
            <label
              htmlFor="dashboard-range"
              className="small text-muted mb-0 mr-2"
            >
              Range
            </label>
            <select
              id="dashboard-range"
              className="form-control form-control-sm mr-2"
              style={{ maxWidth: '170px' }}
              value={range}
              disabled={overviewState.loading}
              onChange={event => setRange(event.target.value)}
            >
              {DASHBOARD_RANGE_OPTIONS.map(option => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              theme="primary"
              disabled={overviewState.loading}
              onClick={refreshDashboard}
            >
              {overviewState.loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </Col>
      </Row>

      {overviewState.error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger d-flex align-items-center justify-content-between">
              <span>{overviewState.error}</span>
              <Button
                size="sm"
                theme="danger"
                onClick={refreshDashboard}
              >
                Retry
              </Button>
            </div>
          </Col>
        </Row>
      )}

      {hasInitialLoad ? (
        <DashboardSkeleton />
      ) : overview && (
        <>
          <Row>
            <CheckPermission resource="user" action={['read']}>
              <Col xl="2" lg="4" md="6" sm="6">
                <StateCard
                  label="Users"
                  value={formatNumber(summary?.usersCount)}
                />
              </Col>
            </CheckPermission>
            <CheckPermission resource="plan" action={['read']}>
              <Col xl="2" lg="4" md="6" sm="6">
                <StateCard
                  label="Active Plans"
                  value={formatNumber(summary?.activePlansCount)}
                  hoverTitle="Active plan definitions"
                />
              </Col>
            </CheckPermission>
            <CheckPermission resource="subscription" action={['read']}>
              <Col xl="2" lg="4" md="6" sm="6">
                <StateCard
                  label="Active Subscriptions"
                  value={formatNumber(summary?.activeSubscriptionsCount)}
                />
              </Col>
            </CheckPermission>
            <CheckPermission resource="payment" action={['read']}>
              <Col xl="2" lg="4" md="6" sm="6">
                <StateCard
                  label="Payments"
                  value={formatNumber(summary?.payments?.count)}
                  hoverTitle={paymentHoverTitle}
                />
              </Col>
            </CheckPermission>
            <CheckPermission resource="design" action={['read']}>
              <Col xl="2" lg="4" md="6" sm="6">
                <StateCard
                  label="Designs"
                  value={formatNumber(summary?.designsCount)}
                />
              </Col>
            </CheckPermission>
            <CheckPermission resource="template" action={['read']}>
              <Col xl="2" lg="4" md="6" sm="6">
                <StateCard
                  label="Templates"
                  value={formatNumber(summary?.templatesCount)}
                />
              </Col>
            </CheckPermission>
          </Row>

          <CheckPermission resource="dashboard" action={["read"]}>
            <Row>
              <Col lg="8" md="12" sm="12" className="mb-4">
                <UsersOverview
                  loading={overviewState.loading}
                  onRangeChange={setRange}
                  range={range}
                  users={
                    range === '7d' || range === '30d'
                      ? overview?.usersSeries?.daily || []
                      : overview?.usersSeries?.monthly || []
                  }
                />
              </Col>
              <Col lg="4" md="6" sm="12" className="mb-4">
                <MonthlyUsers series={overview?.usersSeries?.monthly || []} />
              </Col>
              <Col lg="5" md="6" sm="12" className="mb-4">
                <SubscriptionByPlan
                  subscriptions={overview?.subscriptionsByPlanStatus || []}
                />
              </Col>
              <Col lg="7" md="12" sm="12" className="mb-4">
                <PaymentOverview
                  meta={overview?.paymentsMeta || {}}
                  series={overview?.paymentsSeries || []}
                />
              </Col>
            </Row>
            <Row>
              <Col lg="8" md="12" sm="12" className="mb-4">
                <RetentionOverview series={overview?.retentionSeries || []} />
              </Col>
            </Row>
          </CheckPermission>
        </>
      )}
    </Container>
  );
}

export default BlogOverview;
